import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';

@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  private static readonly DEVICE_ID_KEY = 'device.id';
  private static readonly DEVICE_NAME_KEY = 'device.name';

  constructor() {
    this.initDeviceName();
  }

  getDeviceId(): string {
    let deviceId = localStorage.getItem(DeviceService.DEVICE_ID_KEY);
    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem(DeviceService.DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
  }

  getPlatform(): string {
    if (Capacitor.isNativePlatform()) {
      const platform = Capacitor.getPlatform();
      if (platform === 'ios') return 'ios';
      if (platform === 'android') return 'android';
      return 'native';
    }
    return 'web';
  }

  getDeviceName(): string | null {
    return localStorage.getItem(DeviceService.DEVICE_NAME_KEY);
  }

  setDeviceName(name: string): void {
    localStorage.setItem(DeviceService.DEVICE_NAME_KEY, name);
  }

  getDeviceHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'X-Device-Id': this.getDeviceId(),
      'X-Platform': this.getPlatform(),
    };
    const name = this.getDeviceName();
    if (name) {
      headers['X-Device-Name'] = name;
    }
    const appVersion = (globalThis as any).appVersion;
    if (appVersion) {
      headers['X-App-Version'] = appVersion;
    }
    return headers;
  }

  private initDeviceName(): void {
    if (this.getDeviceName()) {
      return;
    }

    if (Capacitor.isNativePlatform()) {
      Device.getInfo().then((info) => {
        this.setDeviceName(info.name ?? info.model);
      });
    } else {
      this.setDeviceName(this.detectBrowserName());
    }
  }

  private detectBrowserName(): string {
    const ua = navigator.userAgent;

    if (ua.includes('Firefox/')) {
      const version = ua.match(/Firefox\/([\d.]+)/)?.[1];
      return `Firefox ${version}`;
    }
    if (ua.includes('Edg/')) {
      const version = ua.match(/Edg\/([\d.]+)/)?.[1];
      return `Edge ${version}`;
    }
    if (ua.includes('Chrome/') && !ua.includes('Edg/')) {
      const version = ua.match(/Chrome\/([\d.]+)/)?.[1];
      return `Chrome ${version}`;
    }
    if (ua.includes('Safari/') && !ua.includes('Chrome/')) {
      const version = ua.match(/Version\/([\d.]+)/)?.[1];
      return `Safari ${version}`;
    }

    return 'Browser';
  }
}
